import React, { createContext, useReducer } from "react";
import { OverviewReducer } from "../reducers/OverviewReducer";

export const OverviewContext = createContext();

const OverviewContextProvider = (props) => {
  const overviewData = {
    fph: {
      induct: {
        title: "fph.induct_fph",
        defaultMessage: "Induct FPH",
        actual: "0",
        tooltip: "tooltip.overview.inductFPH",
        tooltipMessage: "Trailing 10-min average of package inducted per hour",
        alert: "ocr.alertMessage",
        alertMessage: "Ontime Completion Risk is",
      },
      sort: {
        title: "fph.sort_fph",
        defaultMessage: "Stow FPH",
        actual: "0",
        tooltip: "tooltip.overview.stowFPH",
        tooltipMessage: "Trailing 10-min average of package stowed per hour",
      },
      pick: {
        title: "fph.pick_fph",
        defaultMessage: "Pick FPH",
        actual: "0",
        tooltip: "tooltip.overview.pickFPH",
        tooltipMessage: "Trailing 10-min average of package picked per hour",
      },
      stage: {
        title: "fph.stage_fph",
        defaultMessage: "Stage FPH",
        actual: "0",
        tooltip: "tooltip.overview.stageFPH",
        tooltipMessage: "Trailing 10-min average of package staged per hour",
      },
    },
    compliance: {
      goldilock: {
        title: "compliance_goldilock",
        defaultMessage: "Goldilocks",
        value: "0",
        prefixValue: "%",
        tooltip: "tooltip.overview.goldilock",
        tooltipMessage:
          "Cumulative time that Induct FPH and Stow FPH matched within +/- 5%",
      },
      scanCompliance: {
        title: "compliance.scanCompliance",
        defaultMessage: "Scan compliance",
        value: "0",
        prefixValue: "%",
        tooltip: "tooltip.overview.scanCompliance",
        tooltipMessage:
          "Percentage of inducted packages that have received a successful stow",
      },
      rolledVolume: {
        title: "compliance.rolledVolume",
        defaultMessage: "Rolled volume expected",
        value: "-",
        prefixValue: "",
        tooltip: "tooltip.overview.rve",
        tooltipMessage:
          "Number of predicted dispatch packages that will not be stowed by end of shift at present stow rate",
      },
      shiftPace: {
        title: "compliance.shiftPace",
        defaultMessage: "Shift pace",
        value: "0",
        prefixValue: "mins behind",
        tooltip: "tooltip.overview.shiftPace",
        tooltipMessage:
          "Number of minutes required to stow expected rolled volume at present stow rate",
      },
      dnc: {
        title: "dnc.dnc",
        defaultMessage: "Defects not corrected",
        value: "0",
        prefixValue: "",
        tooltip: "tooltip.overview.dnc",
        tooltipMessage:
          "Packages inducted with a FSAF and no successful stow (DPMO)",
      },
    },
    wip: {
      stowWip: {
        title: "wip.stowWip",
        defaultMessage: "Stow WIP",
        actual: "0",
        prefixValue: "mins",
        tooltip: "tooltip.overview.stowWIP",
        tooltipMessage:
          "Total packages inducted not stowed in last 15 minutes divided by Stow FPH",
        alert: "wip.alertMessage",
        alertMessage: "Target time to build WIP is",
      },
      wipIntervals: {
        title: "stowWip.wipIntervals",
        defaultMessage: "Stow Work In Progress in 15 minute intervals",
        value: {},
        tooltip: "tooltip.overview.stowWip",
        tooltipMessage:
          "Total packages inducted not stowed divided by packages stowed in last 15 minutes",
      },
      stowWipCompliance: {
        title: "wip.stowWipCompliance",
        defaultMessage: "Est. Stow WIP Compliance",
        actual: "0",
        prefixValue: "",
        tooltip: "tooltip.overview.stowWipCompliance",
        tooltipMessage:
          "Percentage of sortation duration that has compliant stow WIP calculated in 15 min intervals",
      },
      expectedTimeToBuildWip: {
        title: "wip.expectedTimeToBuildWip",
        defaultMessage: "Expected Time to Build WIP",
        value: "0",
        prefixValue: "",
        tooltip: "tooltip.overview.expectedTimeToBuildWip",
        tooltipMessage:
          "Time to build target Stow WIP based on differences in induct and stow rates",
      },
    },
    tv: {
      totalVolume: {
        title: "tv.totalVolume",
        defaultMessage: "Total Volume",
        arrived: "0",
        prefixValue: "",
        tooltip: "tooltip.overview.totalVolume",
        tooltipMessage: "Volume planned for current cycle",
      },
    },
    ocr: {
      ontimeCompletionRisk: {
        title: "ocr.ontimeCompletionRisk",
        defaultMessage: "Ontime Completion Risk",
        value: "0",
        classification: "High",
        prefixAlertMessage: "ocr_alert_message.message",
        prefixAlertDefaultMessage: "Off by",
        postfixAlertMessage: "ocr_alert_message.minutes",
        postAlertDefaultMessage: "minutes",
        tooltip: "tooltip.overview.ontimeCompletionRisk",
        tooltipMessage:
          "Completion risk measured based on projected excess time required for inducting remaining packages",
      },
    },
    hc: {
      induct: {
        title: "hc.induct_hc",
        defaultMessage: "Induct HC",
        actual: "14",
        tooltip: "tooltip.overview.inductHC",
        tooltipMessage: "Headcount for Induct planned vs actual",
      },
      sort: {
        title: "hc.sort_hc",
        defaultMessage: "Stow/P2B HC",
        actual: "24/10",
        tooltip: "tooltip.overview.stowHC",
        tooltipMessage: "Headcount for Stow/Pick to buffer planned vs actual",
      },
    },
    linehaul: {
      linehaulsAtRisk: {
        title: "linehaul.linehaulAtRisk",
        defaultMessage: "Linehauls at risk",
        late: "0",
        cancelled: "0",
        value: null,
        tooltip: "tooltip.overview.linehaulAtRisk",
        tooltipMessage:
          "Linehauls currently at risk of being late or cancelled for this station",
      },
    },
    fa: {
      flowAdherence: {
        title: "fa.flowAdherence",
        defaultMessage: "Flow adherence",
        value: "0",
        tooltip: "tooltip.overview.flowAdherence",
        tooltipMessage: "Tooltip for flow adherence",
      },
    },
    eta: {
      ETAToEndOfShift: {
        title: "eta.ETAToEndOfShift",
        defaultMessage: "ETA to end of shift",
        tooltip: "tooltip.overview.ETAToEndOfShift",
        tooltipMessage:
          "Estimated time for completion of Induct and stow for the given cycle",
      },
    },
    simulate: {
      sort: {
        title: "Shift update",
        description: "simulate.description",
        defaultMessage:
          "Adjust HC or Volume. Link will be sent to you on slack within 3-5 mins to check the results.",
      },
    },
  };
  const [viewModel, dispatch] = useReducer(OverviewReducer, overviewData);
  return (
    <OverviewContext.Provider value={{ viewModel, dispatch }}>
      {props.children}
    </OverviewContext.Provider>
  );
};

export default OverviewContextProvider;



// WEBPACK FOOTER //
// ./src/components/overview/context/OverviewContext.js